import { installProductNavigation } from "./session/install-product-navigation";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  QueryErrorResetBoundary,
  type Query,
} from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { shouldRetryQuery } from "@softmax/web-features/data";
import {
  parsePageSearch,
  stringifyPageSearch,
} from "@softmax/web-addresses/search";

import { routeTree } from "./routeTree.gen";
import {
  ProductRouteError,
  ProductRoutePending,
} from "./session/product-route-frame";
import { ProductRouteNotFound } from "./session/product-route-error";
import { isRouteAccessDenied } from "./session/route-query";

// Public reads do not depend on who is asking, so losing the viewer must not take them down.
function isPublicQuery(query: Query) {
  return query.queryKey[0] === "public";
}

export function getRouter() {
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (!isRouteAccessDenied(error)) return;

        const principalDenied =
          query.queryKey[0] === "viewer" && query.queryKey[1] === "actual";

        if (principalDenied) {
          void queryClient.cancelQueries(
            {
              predicate: (pending) =>
                pending !== query && !isPublicQuery(pending),
            },
            { revert: false },
          );
          queryClient.getMutationCache().clear();
          router.clearCache();
        }

        const deniedQueries = principalDenied
          ? queryClient
              .getQueryCache()
              .getAll()
              .filter((cached) => !isPublicQuery(cached))
          : [query];

        for (const denied of deniedQueries)
          denied.setState({ data: undefined, dataUpdatedAt: 0 });
      },
    }),
    defaultOptions: { queries: { retry: shouldRetryQuery } },
  });
  const router = createRouter({
    routeTree,
    Wrap: ({ children }) => (
      <QueryClientProvider client={queryClient}>
        <QueryErrorResetBoundary>{children}</QueryErrorResetBoundary>
      </QueryClientProvider>
    ),
    context: {
      queryClient,
    },
    parseSearch: parsePageSearch,
    stringifySearch: stringifyPageSearch,
    scrollRestoration: true,
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    defaultStaleTime: 0,
    defaultGcTime: 0,
    defaultPendingMs: 0,
    defaultPendingMinMs: 0,
    defaultPendingComponent: ProductRoutePending,
    defaultErrorComponent: ProductRouteError,
    defaultNotFoundComponent: ProductRouteNotFound,
  });

  // Router needs explicit route boundaries for not-found errors thrown while rendering.
  for (const route of Object.values(router.routesById)) {
    if (!route.options.notFoundComponent)
      route.update({ notFoundComponent: ProductRouteNotFound });
  }

  if (typeof document !== "undefined") installProductNavigation(router);

  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
